/**
 * Staff Dashboard — PWA-ready, per-person dashboard accessed via QR code.
 * Route: /staff?token=xyz
 * Supports: Housekeeping, Maintenance, Front Desk
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  User, CheckCircle, Clock, AlertTriangle, Play,
  Sparkles, Wrench, BedDouble, RefreshCw, LogOut,
  Wallet, Bell, ChevronRight, Zap, Shield,
  CircleDot, Loader2, Plus
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';
import { toast } from 'sonner';
import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import { useSmartAccount } from '@/hooks/useSmartAccount';

const DEPT_CONFIG = {
  housekeeping: { label: 'Housekeeping', icon: Sparkles, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  front_desk: { label: 'Front Desk', icon: BedDouble, color: 'text-blue-400', bg: 'bg-blue-500/10' },
};

const PRIORITY_STYLES = {
  urgent: 'border-red-500 bg-red-500/5 text-red-400',
  high: 'border-orange-500 bg-orange-500/5 text-orange-400',
  medium: 'border-amber-500 bg-amber-500/5 text-amber-400',
  low: 'border-blue-500 bg-blue-500/5 text-blue-400',
};

function TaskCard({ task, onStart, onComplete }) {
  const isEscalated = task.escalated;
  return (
    <div className={cn("border-l-3 rounded-r-xl p-3.5 transition-all", PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium)}
      data-testid={`task-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">{task.title}</p>
          {task.description && <p className="text-[11px] text-[#94a3b8] mt-0.5">{task.description}</p>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Badge className={cn("text-[8px]", PRIORITY_STYLES[task.priority])}>{task.priority}</Badge>
            {task.room_number && <Badge className="text-[8px] bg-white/5 border-white/10">Room {task.room_number}</Badge>}
            {isEscalated && <Badge className="text-[8px] bg-purple-500/10 text-purple-400 border-purple-500/20">Escalated</Badge>}
            <Badge className="text-[8px] bg-white/5 border-white/10">{task.source}</Badge>
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {task.status === 'pending' && (
            <Button size="sm" onClick={() => onStart(task.id)}
              className="h-7 text-[10px] bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/20 px-2.5"
              data-testid={`start-${task.id}`}>
              <Play className="w-3 h-3 mr-1" /> Start
            </Button>
          )}
          {task.status === 'in_progress' && (
            <Button size="sm" onClick={() => onComplete(task.id)}
              className="h-7 text-[10px] bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/20 px-2.5"
              data-testid={`complete-${task.id}`}>
              <CheckCircle className="w-3 h-3 mr-1" /> Done
            </Button>
          )}
          {task.status === 'completed' && (
            <Badge className="text-[8px] bg-emerald-500/10 text-emerald-400">
              <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Done
            </Badge>
          )}
        </div>
      </div>
      <p className="text-[9px] text-[#475569] mt-1.5 font-mono">{task.created_at?.slice(0, 16).replace('T', ' ')}</p>
    </div>
  );
}

export default function StaffDashboardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [tab, setTab] = useState('active'); // active, completed, all

  // Reown AppKit + Account Abstraction for embedded wallet
  const { open: openAppKit } = useAppKit();
  const { address: walletAddress, isConnected: walletConnected } = useAppKitAccount();
  const { smartAccount, initSmartAccount, loading: aaLoading } = useSmartAccount();
  const [walletSaving, setWalletSaving] = useState(false);

  // When wallet connects via Reown, save to staff profile + create smart account
  useEffect(() => {
    if (!walletConnected || !walletAddress || !staff) return;
    if (staff.wallet_address === walletAddress) return; // already saved

    const saveWallet = async () => {
      setWalletSaving(true);
      try {
        const jwt = localStorage.getItem('staff_token');
        // Save wallet to staff profile
        await fetch(`${BACKEND_URL}/api/staff/me/wallet?wallet_address=${walletAddress}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${jwt}` },
        });
        // Create/retrieve AA smart account
        await initSmartAccount(walletAddress, staff.email);
        // Refresh profile
        await fetchProfile(jwt);
        toast.success('Wallet created! You can now receive HERMES tokens.');
      } catch {
        toast.error('Failed to save wallet');
      } finally {
        setWalletSaving(false);
      }
    };
    saveWallet();
  }, [walletConnected, walletAddress, staff]);

  // Check for QR token in URL or stored JWT
  useEffect(() => {
    const qrToken = searchParams.get('token');
    if (qrToken) {
      loginWithQR(qrToken);
    } else {
      const stored = localStorage.getItem('staff_token');
      if (stored) {
        fetchProfile(stored);
      } else {
        setAuthError('Scan your QR code to access your dashboard');
        setLoading(false);
      }
    }
  }, [searchParams]);

  const loginWithQR = async (qrToken) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/qr-login/${qrToken}`);
      if (!res.ok) throw new Error('Invalid QR code');
      const data = await res.json();
      localStorage.setItem('staff_token', data.token);
      setStaff(data.staff);
      fetchTasks(data.token);
      // Remove token from URL for cleanliness
      window.history.replaceState({}, '', '/staff');
    } catch {
      setAuthError('Invalid or expired QR code. Please ask your manager for a new one.');
      setLoading(false);
    }
  };

  const fetchProfile = async (token) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Session expired');
      const data = await res.json();
      setStaff(data);
      fetchTasks(token);
    } catch {
      localStorage.removeItem('staff_token');
      setAuthError('Session expired. Scan your QR code again.');
      setLoading(false);
    }
  };

  const fetchTasks = async (token) => {
    const jwt = token || localStorage.getItem('staff_token');
    if (!jwt) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/tasks/my`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const data = await res.json();
      setTasks(data.tasks || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const startTask = async (taskId) => {
    const jwt = localStorage.getItem('staff_token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/tasks/${taskId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.ok) {
        toast.success('Task started');
        fetchTasks(jwt);
      }
    } catch { toast.error('Failed to start task'); }
  };

  const completeTask = async (taskId) => {
    const jwt = localStorage.getItem('staff_token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/tasks/${taskId}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.ok) {
        toast.success('Task completed! +1 reputation');
        fetchTasks(jwt);
      }
    } catch { toast.error('Failed to complete task'); }
  };

  const logout = () => {
    localStorage.removeItem('staff_token');
    setStaff(null);
    setTasks([]);
    setAuthError('Scan your QR code to access your dashboard');
  };

  // Auto-refresh every 30s
  useEffect(() => {
    if (!staff) return;
    const iv = setInterval(() => fetchTasks(), 30000);
    return () => clearInterval(iv);
  }, [staff]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#64748b]">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-mono">Loading your dashboard...</span>
        </div>
      </div>
    );
  }

  if (authError || !staff) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center p-4" data-testid="staff-qr-login">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Staff Dashboard</h1>
          <p className="text-sm text-[#94a3b8] mb-6">{authError || 'Scan your QR code to log in'}</p>
          <p className="text-[10px] text-[#475569]">Powered by getbitsy.ai</p>
        </div>
      </div>
    );
  }

  const dept = DEPT_CONFIG[staff.department] || DEPT_CONFIG.front_desk;
  const DeptIcon = dept.icon;
  const activeTasks = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const displayTasks = tab === 'active' ? activeTasks : tab === 'completed' ? completedTasks : tasks;

  return (
    <div className="lobby-theme min-h-screen" data-testid="staff-dashboard">
      {/* Header */}
      <div className="border-b border-[#1a2235] bg-[#0a0f1d]/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("w-9 h-9 rounded-xl border flex items-center justify-center", dept.bg, `border-${dept.color.replace('text-', '')}/30`)}>
              <DeptIcon className={cn("w-4.5 h-4.5", dept.color)} />
            </div>
            <div>
              <h1 className="text-sm font-bold" data-testid="staff-name">{staff.name}</h1>
              <p className="text-[9px] text-[#475569]">{dept.label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeTasks.length > 0 && (
              <Badge className="text-[9px] bg-amber-500/10 text-amber-400 animate-pulse">
                {activeTasks.length} active
              </Badge>
            )}
            <Button size="sm" variant="ghost" onClick={() => fetchTasks()}
              className="h-7 w-7 p-0 text-[#64748b]">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={logout}
              className="h-7 w-7 p-0 text-[#64748b]" data-testid="staff-logout">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-3 text-center">
            <p className="text-xl font-bold font-mono text-amber-400">{activeTasks.filter(t => t.status === 'pending').length}</p>
            <p className="text-[9px] text-[#64748b]">Pending</p>
          </div>
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-3 text-center">
            <p className="text-xl font-bold font-mono text-blue-400">{activeTasks.filter(t => t.status === 'in_progress').length}</p>
            <p className="text-[9px] text-[#64748b]">In Progress</p>
          </div>
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-3 text-center">
            <p className="text-xl font-bold font-mono text-emerald-400">{staff.tasks_completed || 0}</p>
            <p className="text-[9px] text-[#64748b]">Completed</p>
          </div>
        </div>

        {/* Wallet + Reputation */}
        <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-3" data-testid="staff-wallet">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className={cn("w-4 h-4", dept.color)} />
              <div>
                <p className="text-[10px] text-[#64748b]">Reputation</p>
                <p className="text-sm font-bold font-mono">{staff.reputation_score || 0} <span className="text-[9px] text-[#475569]">HERMES</span></p>
              </div>
            </div>
            {(staff.wallet_address || walletAddress) ? (
              <div className="flex flex-col items-end gap-1">
                <Badge className="text-[8px] bg-emerald-500/10 text-emerald-400">
                  <Wallet className="w-2.5 h-2.5 mr-0.5" /> {(staff.wallet_address || walletAddress).slice(0, 6)}...{(staff.wallet_address || walletAddress).slice(-4)}
                </Badge>
                {smartAccount?.smart_account_address && (
                  <Badge className="text-[7px] bg-purple-500/10 text-purple-400 border-purple-500/20">
                    <Shield className="w-2 h-2 mr-0.5" /> Smart Account
                  </Badge>
                )}
              </div>
            ) : (
              <Button size="sm" onClick={() => openAppKit()} disabled={walletSaving || aaLoading}
                className="h-7 text-[10px] bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 border border-purple-500/20 px-3"
                data-testid="create-wallet-btn">
                {(walletSaving || aaLoading) ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Creating...</>
                ) : (
                  <><Plus className="w-3 h-3 mr-1" /> Create Wallet</>
                )}
              </Button>
            )}
          </div>
          {!staff.wallet_address && !walletAddress && (
            <p className="text-[9px] text-[#475569] mt-2">
              Create a wallet with just your email — no seed phrases, no app downloads. Receive HERMES tokens for completed tasks.
            </p>
          )}
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 bg-[#0d1220] border border-[#1a2235] rounded-xl p-1">
          {[
            { key: 'active', label: `Active (${activeTasks.length})` },
            { key: 'completed', label: `Done (${completedTasks.length})` },
            { key: 'all', label: 'All' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn("flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all",
                tab === t.key ? "bg-white/10 text-white" : "text-[#475569] hover:text-[#94a3b8]"
              )} data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Task List */}
        <div className="space-y-2">
          {displayTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-8 h-8 text-emerald-500/30 mx-auto mb-2" />
              <p className="text-sm text-[#475569]">
                {tab === 'active' ? 'No active tasks — you\'re all caught up!' : 'No tasks yet'}
              </p>
            </div>
          ) : (
            displayTasks.map(task => (
              <TaskCard key={task.id} task={task} onStart={startTask} onComplete={completeTask} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
